export type MainAreaId = "library" | "parlor" | "lounge" | "storage";

export interface MainAreaConfig {
  id: MainAreaId;
  label: string;
  bottleType: "open" | "retail";
  bottleTypeTag: string;
  icon: string;
  subTitle: string;
  defaultZoneId: string;
  zoneIds: string[];
}

export const MAIN_AREAS: MainAreaConfig[] = [
  {
    id: "library",
    label: "Library",
    bottleType: "retail",
    bottleTypeTag: "📚 Open & Retail Bottles",
    icon: "📚",
    subTitle: "Retail Shelf, Open Shelf, Bar Shelf, Cabinet, & Retail Allocated displays",
    defaultZoneId: "LIB",
    zoneIds: ["LIB", "LIB-O", "BAR", "LIB-C", "LIB-A"],
  },
  {
    id: "parlor",
    label: "Parlor",
    bottleType: "open",
    bottleTypeTag: "🟢 Open Bottles",
    icon: "🍸",
    subTitle: "Parlor back wall for open vintage pours & rare allocated barrels",
    defaultZoneId: "RES",
    zoneIds: ["RES"],
  },
  {
    id: "lounge",
    label: "Lounge",
    bottleType: "open",
    bottleTypeTag: "🟢 Open Bottles",
    icon: "✨",
    subTitle: "Speakeasy back-bar glowing tiered display for active service",
    defaultZoneId: "SR",
    zoneIds: ["SR"],
  },
  {
    id: "storage",
    label: "Storage",
    bottleType: "retail",
    bottleTypeTag: "📦 Back Stock / Cases",
    icon: "📦",
    subTitle: "Back storage room shelving for unopened case stock & backup inventory",
    defaultZoneId: "STOR",
    zoneIds: ["STOR"],
  },
];

export interface ZoneConfig {
  id: string;
  areaId: MainAreaId;
  label: string;
  subLabel: string;
  prefix: string;
  cols: number;
  rows: string; // e.g. "ABCDE" (A at bottom, E at top)
  gapsAfter?: number[]; // visual aisle split columns
  columnGaps?: Record<number, number>; // custom width in px for specific gap columns
  icon: string;
  bottleType: "open" | "retail";
  description: string;
}

export const SHELF_ZONES: ZoneConfig[] = [
  {
    id: "LIB",
    areaId: "library",
    label: "Retail Shelf",
    subLabel: "Main 9×9 Retail Display Wall",
    prefix: "LIB",
    cols: 9,
    rows: "ABCDEFGHI",
    gapsAfter: [4, 8],
    icon: "📚",
    bottleType: "retail",
    description: "9 Columns × 9 Tiers — Main library display wall of sealed retail bottles (LIB-1A to LIB-9I)",
  },
  {
    id: "LIB-O",
    areaId: "library",
    label: "Open Shelf",
    subLabel: "Library Open Pour Bottles",
    prefix: "LIB-O",
    cols: 2,
    rows: "ABCDE",
    icon: "🥃",
    bottleType: "open",
    description: "2 Columns × 5 Tiers — Library open shelf for active pour bottles (LIB-O-1A to LIB-O-2E)",
  },
  {
    id: "BAR",
    areaId: "library",
    label: "Bar Shelf",
    subLabel: "Library Bar Shelves",
    prefix: "BAR",
    cols: 2,
    rows: "ABC",
    icon: "🍸",
    bottleType: "open",
    description: "2 Columns × 3 Tiers — Library bar shelf for active open cocktail pours & spirits (BAR-1A to BAR-2C)",
  },
  {
    id: "LIB-C",
    areaId: "library",
    label: "Cabinet",
    subLabel: "Library VIP Lockers & Cabinet",
    prefix: "LIB-C",
    cols: 3,
    rows: "AB",
    icon: "🗄️",
    bottleType: "open",
    description: "3 Columns × 2 Tiers — Library cabinet & allocated VIP member bottles (LIB-C-1A to LIB-C-3B)",
  },
  {
    id: "LIB-A",
    areaId: "library",
    label: "Retail Allocated",
    subLabel: "Rare Sealed Retail",
    prefix: "LIB-A",
    cols: 2,
    rows: "ABCDE",
    icon: "🏆",
    bottleType: "retail",
    description: "2 Columns × 5 Tiers — Library allocated locked display of rare sealed retail bottles (LIB-A-1A to LIB-A-2E)",
  },
  {
    id: "RES",
    areaId: "parlor",
    label: "Parlor Back Wall",
    subLabel: "Parlor Rare Pours",
    prefix: "RES",
    cols: 2,
    rows: "ABCDE",
    icon: "🏛️",
    bottleType: "open",
    description: "2 Columns × 5 Tiers — Parlor back wall display for open vintage allocated barrels & rare pours (RES-1A to RES-2E)",
  },
  {
    id: "SR",
    areaId: "lounge",
    label: "Lounge Back-Bar",
    subLabel: "Open Bottles",
    prefix: "SR",
    cols: 2,
    rows: "ABCDE",
    icon: "✨",
    bottleType: "open",
    description: "2 Columns × 5 Tiers — Speakeasy back-bar glowing tiered display for open bottles & pours (SR-1A to SR-2E)",
  },
  {
    id: "STOR",
    areaId: "storage",
    label: "Back Storage Room",
    subLabel: "Case Stock & Backups",
    prefix: "STOR",
    cols: 4,
    rows: "ABCDE",
    gapsAfter: [1, 2],
    columnGaps: { 1: 28, 2: 12 },
    icon: "📦",
    bottleType: "retail",
    description: "4 Columns × 5 Tiers — Back storage room shelving for backup inventory and unopened case stock (STOR-1A to STOR-4E)",
  },
];


/** Uppercase, trimmed, with the legacy `LIB-OPEN` spelling folded onto `LIB-O`. */
function normalizeShelfCode(code: string): string {
  return code.trim().toUpperCase().replace(/^LIB-OPEN(?=-|$)/, "LIB-O");
}

/** Longest prefix wins, so LIB-O / LIB-C / LIB-A are never shadowed by LIB. */
function zoneForCode(normalized: string): ZoneConfig | undefined {
  return [...SHELF_ZONES].sort((a, b) => b.prefix.length - a.prefix.length)
    .find(z => normalized === z.prefix || normalized.startsWith(z.prefix + "-"));
}

/**
 * The order a runner physically walks the room for a restock pick list: Parlor
 * first, then the library shelves in passing order, then storage and the
 * lounge. Only the ORDER is decided here — zone identity and display labels
 * come from SHELF_ZONES.
 *
 * Every zone in SHELF_ZONES must appear here. A zone left out still parses,
 * but the pick list has no slot to render its group in, so its bottles would
 * vanish from the list while still counting toward the item total — the worst
 * possible failure on a restock run. Two things stop that: the coverage test
 * in shelfLocations.test.mjs fails CI (not `npm run build`, which does not run
 * the node tests), and AdminPickList appends any unlisted zone after the known
 * ones rather than dropping it.
 */
export const SHELF_PICK_ORDER: readonly string[] = ["RES", "LIB-C", "BAR", "LIB-O", "LIB", "LIB-A", "STOR", "SR"];

export interface ParsedShelfCode {
  zone: string;
  col: number;
  row: string;
  /** Index into SHELF_PICK_ORDER; zones missing from it sort after every known zone. */
  zoneIdx: number;
}

/** Split a slot code into zone/column/row, or null if it is not a slot code. */
export function parseShelfCode(code: string): ParsedShelfCode | null {
  const normalized = normalizeShelfCode(code);
  const zone = zoneForCode(normalized);
  if (!zone) return null;
  const slot = normalized.slice(zone.prefix.length).match(/^-(\d+)([A-Z])$/);
  if (!slot) return null;
  const zoneIdx = SHELF_PICK_ORDER.indexOf(zone.id);
  return {
    zone: zone.id,
    col: parseInt(slot[1], 10),
    row: slot[2],
    zoneIdx: zoneIdx === -1 ? SHELF_PICK_ORDER.length : zoneIdx,
  };
}

/** Display label for a zone id, falling back to the id itself. */
export function shelfZoneLabel(zoneId: string): string {
  return SHELF_ZONES.find(z => z.id === zoneId)?.label ?? zoneId;
}

export function describeShelfLocation(code: string) {
  const normalized = normalizeShelfCode(code);
  const zone = zoneForCode(normalized);
  // A known prefix alone cannot classify an unrecognized sub-zone as retail.
  const suffix = zone ? normalized.slice(zone.prefix.length) : "";
  const recognized = zone && (!suffix || /^-\d+[A-Z]$/.test(suffix));
  const area = recognized ? MAIN_AREAS.find(a => a.id === zone.areaId) : undefined;
  return {
    code,
    area: area?.label ?? "Unclassified location",
    shelf: recognized ? (zone.id === "LIB-C" ? "VIP / member cabinet" : zone.label) : "",
    status: recognized ? (zone.bottleType === "open" ? "Open-bottle shelf" : "Closed-bottle shelf") : "Status unknown",
    rank: recognized ? (zone.bottleType === "open" ? 0 : 1) : 2,
  };
}

export function sortedShelfLocations(codes: string[]) {
  return [...new Set(codes)].map(describeShelfLocation)
    .sort((a, b) => a.rank - b.rank || a.area.localeCompare(b.area) || a.code.localeCompare(b.code, undefined, { numeric: true }));
}
